import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/layout/Layout';
import { useTranslation } from 'react-i18next';

interface LotePremio {
  id: string;
  campanha: string;
  dataGeracao: string;
  qtdePremiados: number;
  valorTotal: number;
  meioPagamento: 'PIX' | 'TED' | 'Carteira';
  contaUtilizada: string;
  statusFinanceiro: 'Pago' | 'Pendente';
  statusContabil: 'Gerado' | 'Pendente';
  agencia: string;
  documentoGetpoints?: string;
  protocolo: string;
}

interface Premiado {
  cpfCnpj: string;
  nome: string;
  valorIndividual: number;
  dataPagamento: string;
  idTransacao: string;
  canalPagamento: string;
  obrigacaoGerada: boolean;
  pagamentoContaPremio: boolean;
  exportacaoContabil: boolean;
}

interface LancamentoContabil {
  id: string;
  campanha: string;
  competencia: string;
  valorTotal: number;
  status: 'Gerado' | 'Exportado';
  erpDestino: string;
  dataGeracao: string;
  debito: string;
  credito: string;
}

interface NotaFiscalGetpoints {
  id: string;
  agencia: string;
  tipo: 'NF Serviço' | 'Nota Débito';
  numero: string;
  valor: number;
  competencia: string;
  contaPagamento: string;
  status: 'Vinculada' | 'Pendente';
  dataEmissao: string;
}

interface ContaDigital {
  saldoInicial: number;
  depositos: number;
  saques: number;
  pagamentos: number;
  saldoDisponivel: number;
}

export default function ConciliacaoContabilPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'conta-premio' | 'conta-digital'>('conta-premio');

  // Função para abreviar números
  const formatarNumeroAbreviado = (valor: number): string => {
    if (valor >= 1000000) {
      return `${(valor / 1000000).toFixed(1)}M`;
    }
    if (valor >= 1000) {
      return `${(valor / 1000).toFixed(1)}K`;
    }
    return valor.toFixed(2);
  };

  const navigate = useNavigate();
  const [abaAtiva, setAbaAtiva] = useState<'dashboard' | 'lotes' | 'lancamentos' | 'notas' | 'conta' | 'exportacao' | 'guia'>('dashboard');
  const [loteDetalhado, setLoteDetalhado] = useState<string | null>(null);
  const [modalExportacao, setModalExportacao] = useState(false);
  const [formatoExportacao, setFormatoExportacao] = useState<'CSV' | 'Excel' | 'TXT' | 'XML'>('CSV');
  const [modalVincularNF, setModalVincularNF] = useState(false);
  const [loteParaVincular, setLoteParaVincular] = useState<string | null>(null);

  // Mock Data - Lotes de Prêmios
  const [lotesPremios] = useState<LotePremio[]>([
    {
      id: 'LOTE-2024-001',
      campanha: 'Vendas Q4 2024',
      dataGeracao: '2024-03-15',
      qtdePremiados: 45,
      valorTotal: 67500.00,
      meioPagamento: 'PIX',
      contaUtilizada: 'Conta Prêmio - BB 12345-6',
      statusFinanceiro: 'Pago',
      statusContabil: 'Gerado',
      agencia: 'GETPOINTS',
      documentoGetpoints: 'NF-2024-0456',
      protocolo: 'PROT-2024-15678'
    },
    {
      id: 'LOTE-2024-002',
      campanha: 'Meta Mensal Janeiro',
      dataGeracao: '2024-03-14',
      qtdePremiados: 32,
      valorTotal: 48000.00,
      meioPagamento: 'TED',
      contaUtilizada: 'Conta Prêmio - BB 12345-6',
      statusFinanceiro: 'Pago',
      statusContabil: 'Gerado',
      agencia: 'GETPOINTS',
      documentoGetpoints: 'NF-2024-0455',
      protocolo: 'PROT-2024-15677'
    },
    {
      id: 'LOTE-2024-003',
      campanha: 'Indicação de Clientes',
      dataGeracao: '2024-03-13',
      qtdePremiados: 67,
      valorTotal: 100500.00,
      meioPagamento: 'Carteira',
      contaUtilizada: 'Conta Prêmio - BB 12345-6',
      statusFinanceiro: 'Pendente',
      statusContabil: 'Pendente',
      agencia: 'GETPOINTS',
      protocolo: 'PROT-2024-15676'
    },
    {
      id: 'LOTE-2024-004',
      campanha: 'Campanha de Produtividade',
      dataGeracao: '2024-03-12',
      qtdePremiados: 28,
      valorTotal: 42000.00,
      meioPagamento: 'PIX',
      contaUtilizada: 'Conta Prêmio - BB 12345-6',
      statusFinanceiro: 'Pago',
      statusContabil: 'Gerado',
      agencia: 'GETPOINTS',
      documentoGetpoints: 'NF-2024-0454',
      protocolo: 'PROT-2024-15675'
    }
  ]);

  // Mock Data - Premiados do Lote
  const [premiadosLote] = useState<{ [key: string]: Premiado[] }>({
    'LOTE-2024-001': [
      {
        cpfCnpj: '123.456.789-00',
        nome: 'Ana Silva Santos',
        valorIndividual: 1500.00,
        dataPagamento: '2024-03-15',
        idTransacao: 'TXN-2024-001',
        canalPagamento: 'PIX',
        obrigacaoGerada: true,
        pagamentoContaPremio: true,
        exportacaoContabil: true
      },
      {
        cpfCnpj: '987.654.321-00',
        nome: 'Carlos Eduardo Lima',
        valorIndividual: 2000.00,
        dataPagamento: '2024-03-15',
        idTransacao: 'TXN-2024-002',
        canalPagamento: 'PIX',
        obrigacaoGerada: true,
        pagamentoContaPremio: true,
        exportacaoContabil: true
      },
      {
        cpfCnpj: '456.789.123-00',
        nome: 'Maria Oliveira Costa',
        valorIndividual: 1800.00,
        dataPagamento: '2024-03-15',
        idTransacao: 'TXN-2024-003',
        canalPagamento: 'PIX',
        obrigacaoGerada: true,
        pagamentoContaPremio: true,
        exportacaoContabil: true
      }
    ]
  });

  // Mock Data - Lançamentos Contábeis
  const [lancamentosContabeis] = useState<LancamentoContabil[]>([
    {
      id: 'LANC-2024-001',
      campanha: 'Vendas Q4 2024',
      competencia: '03/2024',
      valorTotal: 67500.00,
      status: 'Gerado',
      erpDestino: 'SAP',
      dataGeracao: '2024-03-15',
      debito: 'Despesa com Prêmios – Campanha',
      credito: 'Obrigações com Premiados'
    },
    {
      id: 'LANC-2024-002',
      campanha: 'Meta Mensal Janeiro',
      competencia: '03/2024',
      valorTotal: 48000.00,
      status: 'Exportado',
      erpDestino: 'Protheus',
      dataGeracao: '2024-03-14',
      debito: 'Despesa com Prêmios – Campanha',
      credito: 'Obrigações com Premiados'
    },
    {
      id: 'LANC-2024-003',
      campanha: 'Campanha de Produtividade',
      competencia: '03/2024',
      valorTotal: 42000.00,
      status: 'Gerado',
      erpDestino: 'SAP',
      dataGeracao: '2024-03-12',
      debito: 'Despesa com Prêmios – Campanha',
      credito: 'Obrigações com Premiados'
    }
  ]);

  // Mock Data - Notas Fiscais GETPOINTS
  const [notasGetpoints] = useState<NotaFiscalGetpoints[]>([
    {
      id: 'NF-2024-0456',
      agencia: 'GETPOINTS',
      tipo: 'NF Serviço',
      numero: '456',
      valor: 6750.00,
      competencia: '03/2024',
      contaPagamento: 'Conta Operacional - BB 54321-9',
      status: 'Vinculada',
      dataEmissao: '2024-03-15'
    },
    {
      id: 'NF-2024-0455',
      agencia: 'GETPOINTS',
      tipo: 'NF Serviço',
      numero: '455',
      valor: 4800.00,
      competencia: '03/2024',
      contaPagamento: 'Conta Operacional - BB 54321-9',
      status: 'Vinculada',
      dataEmissao: '2024-03-14'
    },
    {
      id: 'NF-2024-0454',
      agencia: 'GETPOINTS',
      tipo: 'Nota Débito',
      numero: '454',
      valor: 4200.00,
      competencia: '03/2024',
      contaPagamento: 'Conta Operacional - BB 54321-9',
      status: 'Vinculada',
      dataEmissao: '2024-03-12'
    },
    {
      id: 'NF-2024-0453',
      agencia: 'GETPOINTS',
      tipo: 'NF Serviço',
      numero: '453',
      valor: 3500.00,
      competencia: '03/2024',
      contaPagamento: 'Conta Operacional - BB 54321-9',
      status: 'Pendente',
      dataEmissao: '2024-03-10'
    }
  ]);

  // Dados da Conta Prêmio
  const contaPremio = {
    saldoInicial: 250000.00,
    aportes: 150000.00,
    premiosPagos: 157500.00,
    obrigacoesAberto: 100500.00,
    saldoDisponivel: 242500.00
  };

  // Dados da Conta Digital
  const contaDigital: ContaDigital = {
    saldoInicial: 180000.00,
    depositos: 95000.00,
    saques: 42000.00,
    pagamentos: 68000.00,
    saldoDisponivel: 165000.00
  };

  // KPIs do Dashboard
  const totalPremiosGerados = lotesPremios.reduce((sum, lote) => sum + lote.valorTotal, 0);
  const totalPagoContaPremio = lotesPremios
    .filter(lote => lote.statusFinanceiro === 'Pago')
    .reduce((sum, lote) => sum + lote.valorTotal, 0);
  const totalContabilizado = lancamentosContabeis
    .filter(lanc => lanc.status === 'Gerado' || lanc.status === 'Exportado')
    .reduce((sum, lanc) => sum + lanc.valorTotal, 0);
  const lancamentosPendentes = lotesPremios.filter(lote => lote.statusContabil === 'Pendente').length;
  const divergenciaFinanceira = totalPremiosGerados - totalPagoContaPremio;

  // Alertas Automáticos
  const alertas = [
    {
      tipo: 'warning',
      titulo: 'Pagamento sem lançamento contábil',
      descricao: '1 lote pago aguardando geração de lançamento contábil',
      quantidade: 1
    },
    {
      tipo: 'info',
      titulo: 'NF GETPOINTS não vinculada',
      descricao: '1 nota fiscal da GETPOINTS pendente de vinculação',
      quantidade: 1
    }
  ];

  const handleGerarLancamento = (loteId: string) => {
    alert(`Lançamento contábil gerado para o lote ${loteId}`);
  };

  const handleVincularNF = () => {
    alert('Nota fiscal vinculada com sucesso!');
    setModalVincularNF(false);
    setLoteParaVincular(null);
  };

  const handleExportar = () => {
    alert(`Exportando dados no formato ${formatoExportacao}...`);
    setModalExportacao(false);
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Prêmios Gerados</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                R$ {formatarNumeroAbreviado(totalPremiosGerados)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-gift-line text-white text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Pago via Conta Prêmio</p>
              <p className="text-2xl font-bold text-emerald-600 mt-2">
                R$ {formatarNumeroAbreviado(totalPagoContaPremio)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-emerald-500 flex items-center justify-center">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-bank-card-line text-white text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Contabilizado</p>
              <p className="text-2xl font-bold text-purple-600 mt-2">
                R$ {formatarNumeroAbreviado(totalContabilizado)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-500 flex items-center justify-center">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-file-chart-line text-white text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Lançamentos Pendentes</p>
              <p className="text-2xl font-bold text-orange-600 mt-2">{lancamentosPendentes}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-orange-500 flex items-center justify-center">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-time-line text-white text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Divergência Financeira</p>
              <p className="text-2xl font-bold text-red-600 mt-2">
                R$ {Math.abs(divergenciaFinanceira).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-red-500 flex items-center justify-center">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-error-warning-line text-white text-xl"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alertas Automáticos */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Alertas Automáticos</h3>
        <div className="space-y-3">
          {alertas.map((alerta, index) => (
            <div
              key={index}
              className={`flex items-start space-x-3 p-4 rounded-lg ${
                alerta.tipo === 'warning' ? 'bg-yellow-50 border border-yellow-200' : 'bg-blue-50 border border-blue-200'
              }`}
            >
              <div className={`w-5 h-5 flex items-center justify-center mt-0.5 ${
                alerta.tipo === 'warning' ? 'text-yellow-600' : 'text-blue-600'
              }`}>
                <i className={`ri-${alerta.tipo === 'warning' ? 'alert' : 'information'}-line`}></i>
              </div>
              <div className="flex-1">
                <h4 className={`font-medium ${alerta.tipo === 'warning' ? 'text-yellow-900' : 'text-blue-900'}`}>
                  {alerta.titulo}
                </h4>
                <p className={`text-sm mt-1 ${alerta.tipo === 'warning' ? 'text-yellow-700' : 'text-blue-700'}`}>
                  {alerta.descricao}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                alerta.tipo === 'warning' ? 'bg-yellow-200 text-yellow-800' : 'bg-blue-200 text-blue-800'
              }`}>
                {alerta.quantidade}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Resumo Visual */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fluxo Contábil</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                1
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Aporte na Conta Prêmio</p>
                <p className="text-sm text-gray-600">D: Caixa/Bancos – Conta Prêmio | C: Caixa/Bancos – Conta Operacional</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                2
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Reconhecimento do Prêmio</p>
                <p className="text-sm text-gray-600">D: Despesa com Prêmios | C: Obrigações com Premiados</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                3
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Pagamento do Prêmio</p>
                <p className="text-sm text-gray-600">D: Obrigações com Premiados | C: Caixa/Bancos – Conta Prêmio</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                4
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Pagamento GETPOINTS</p>
                <p className="text-sm text-gray-600">D: Despesa Serviços GETPOINTS | C: Fornecedores GETPOINTS</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Status dos Lotes</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-white"></i>
                  </div>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Pagos e Contabilizados</p>
                  <p className="text-sm text-gray-600">Lotes concluídos</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-emerald-600">
                {lotesPremios.filter(l => l.statusFinanceiro === 'Pago' && l.statusContabil === 'Gerado').length}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-time-line text-white"></i>
                  </div>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Pendentes</p>
                  <p className="text-sm text-gray-600">Aguardando processamento</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-orange-600">
                {lotesPremios.filter(l => l.statusFinanceiro === 'Pendente' || l.statusContabil === 'Pendente').length}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLotesPremios = () => (
    <div className="space-y-6">
      {loteDetalhado ? (
        // Detalhe do Lote
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setLoteDetalhado(null)}
                className="text-blue-600 hover:text-blue-800 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
              </button>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Detalhes do Lote {loteDetalhado}</h3>
                <p className="text-gray-600">Informações completas dos premiados</p>
              </div>
            </div>
          </div>

          {/* Resumo do Lote */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Campanha</p>
                <p className="font-semibold text-gray-900 mt-1">
                  {lotesPremios.find(l => l.id === loteDetalhado)?.campanha}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Data de Geração</p>
                <p className="font-semibold text-gray-900 mt-1">
                  {new Date(lotesPremios.find(l => l.id === loteDetalhado)?.dataGeracao || '').toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Premiados</p>
                <p className="font-semibold text-gray-900 mt-1">
                  {lotesPremios.find(l => l.id === loteDetalhado)?.qtdePremiados}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Valor Total</p>
                <p className="font-semibold text-blue-600 mt-1">
                  R$ {lotesPremios.find(l => l.id === loteDetalhado)?.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {/* Tabela de Premiados */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CPF/CNPJ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data Pagamento</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID Transação</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conciliação</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(premiadosLote[loteDetalhado] || []).map((premiado, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{premiado.cpfCnpj}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{premiado.nome}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-emerald-600">
                        R$ {premiado.valorIndividual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(premiado.dataPagamento).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{premiado.idTransacao}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {premiado.canalPagamento}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className={`w-5 h-5 flex items-center justify-center ${premiado.obrigacaoGerada ? 'text-emerald-600' : 'text-gray-400'}`}>
                            <i className={`ri-${premiado.obrigacaoGerada ? 'checkbox-circle-fill' : 'close-circle-line'}`}></i>
                          </div>
                          <div className={`w-5 h-5 flex items-center justify-center ${premiado.pagamentoContaPremio ? 'text-emerald-600' : 'text-gray-400'}`}>
                            <i className={`ri-${premiado.pagamentoContaPremio ? 'checkbox-circle-fill' : 'close-circle-line'}`}></i>
                          </div>
                          <div className={`w-5 h-5 flex items-center justify-center ${premiado.exportacaoContabil ? 'text-emerald-600' : 'text-gray-400'}`}>
                            <i className={`ri-${premiado.exportacaoContabil ? 'checkbox-circle-fill' : 'close-circle-line'}`}></i>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        // Lista de Lotes
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID do Lote</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campanha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data Geração</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Premiados</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meio Pagamento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status Financeiro</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status Contábil</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agência</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lotesPremios.map((lote) => (
                  <tr key={lote.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">{lote.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{lote.campanha}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(lote.dataGeracao).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{lote.qtdePremiados}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-emerald-600">
                      R$ {lote.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {lote.meioPagamento}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        lote.statusFinanceiro === 'Pago' ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {lote.statusFinanceiro}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        lote.statusContabil === 'Gerado' ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {lote.statusContabil}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                        {lote.agencia}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setLoteDetalhado(lote.id)}
                          className="text-blue-600 hover:text-blue-800 cursor-pointer"
                          title="Ver premiados"
                        >
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </div>
                        </button>
                        {!lote.documentoGetpoints && (
                          <button
                            onClick={() => {
                              setLoteParaVincular(lote.id);
                              setModalVincularNF(true);
                            }}
                            className="text-orange-600 hover:text-orange-800 cursor-pointer"
                            title="Vincular NF GETPOINTS"
                          >
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-link"></i>
                            </div>
                          </button>
                        )}
                        {lote.statusContabil === 'Pendente' && (
                          <button
                            onClick={() => handleGerarLancamento(lote.id)}
                            className="text-emerald-600 hover:text-emerald-800 cursor-pointer"
                            title="Gerar lançamento contábil"
                          >
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-file-add-line"></i>
                            </div>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderLancamentosContabeis = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campanha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Competência</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Débito</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Crédito</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ERP Destino</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data Geração</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lancamentosContabeis.map((lancamento) => (
                <tr key={lancamento.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">{lancamento.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{lancamento.campanha}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{lancamento.competencia}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{lancamento.debito}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{lancamento.credito}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-purple-600">
                    R$ {lancamento.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      lancamento.status === 'Exportado' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {lancamento.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{lancamento.erpDestino}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(lancamento.dataGeracao).toLocaleDateString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderNotasGetpoints = () => (
    <div className="space-y-6">
      {/* Aviso Importante */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-600">
            <i className="ri-error-warning-line"></i>
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-red-900 mb-1">Atenção - Regras Críticas</h4>
            <ul className="text-sm text-red-800 space-y-1">
              <li>🚫 <strong>NUNCA</strong> associar a Conta Prêmio</li>
              <li>🚫 <strong>NUNCA</strong> associar a lançamento de prêmio</li>
              <li>✅ Pagamento sempre via Conta Operacional</li>
              <li>✅ Agência fixa e imutável: GETPOINTS</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agência</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Número</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Competência</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conta Pagamento</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data Emissão</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {notasGetpoints.map((nota) => (
                <tr key={nota.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                      {nota.agencia}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      nota.tipo === 'NF Serviço' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {nota.tipo}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{nota.numero}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-emerald-600">
                    R$ {nota.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{nota.competencia}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{nota.contaPagamento}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      nota.status === 'Vinculada' ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {nota.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(nota.dataEmissao).toLocaleDateString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderContaPremio = () => (
    <div className="space-y-6">
      {/* Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">Saldo Inicial</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">
            R$ {formatarNumeroAbreviado(contaPremio.saldoInicial)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">Aportes</p>
          <p className="text-2xl font-bold text-blue-600 mt-2">
            R$ {formatarNumeroAbreviado(contaPremio.aportes)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">Prêmios Pagos</p>
          <p className="text-2xl font-bold text-red-600 mt-2">
            R$ {formatarNumeroAbreviado(contaPremio.premiosPagos)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">Obrigações em Aberto</p>
          <p className="text-2xl font-bold text-orange-600 mt-2">
            R$ {formatarNumeroAbreviado(contaPremio.obrigacoesAberto)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">Saldo Disponível</p>
          <p className="text-2xl font-bold text-green-600 mt-2">
            R$ {formatarNumeroAbreviado(contaPremio.saldoDisponivel)}
          </p>
        </div>
      </div>

      {/* Bloqueios e Regras */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-900 mb-4">Bloqueios de Segurança</h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-600">
              <i className="ri-shield-cross-line"></i>
            </div>
            <div>
              <p className="font-medium text-red-900">Destinatário ≠ premiado → BLOQUEADO</p>
              <p className="text-sm text-red-700">Pagamentos só podem ser feitos para premiados cadastrados</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-600">
              <i className="ri-shield-cross-line"></i>
            </div>
            <div>
              <p className="font-medium text-red-900">Destinatário = GETPOINTS → BLOQUEADO</p>
              <p className="text-sm text-red-700">Pagamentos para GETPOINTS devem ser feitos pela Conta Operacional</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-600">
              <i className="ri-shield-cross-line"></i>
            </div>
            <div>
              <p className="font-medium text-red-900">Uso exclusivo para premiados</p>
              <p className="text-sm text-red-700">Proibido pagamento de fornecedores, serviços ou agência</p>
            </div>
          </div>
        </div>
      </div>

      {/* Informações da Conta */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações da Conta</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600">Banco</p>
            <p className="font-medium text-gray-900 mt-1">Banco do Brasil</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Agência</p>
            <p className="font-medium text-gray-900 mt-1">1234-5</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Conta</p>
            <p className="font-medium text-gray-900 mt-1">12345-6</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Tipo</p>
            <p className="font-medium text-gray-900 mt-1">Conta Corrente Segregada</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Finalidade</p>
            <p className="font-medium text-gray-900 mt-1">Pagamento Exclusivo de Prêmios</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Status</p>
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800 mt-1">
              Ativa
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContaDigital = () => (
    <div className="space-y-6">
      {/* Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">Saldo Inicial</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">
            R$ {formatarNumeroAbreviado(contaDigital.saldoInicial)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">Depósitos</p>
          <p className="text-2xl font-bold text-blue-600 mt-2">
            R$ {formatarNumeroAbreviado(contaDigital.depositos)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">Saques</p>
          <p className="text-2xl font-bold text-red-600 mt-2">
            R$ {formatarNumeroAbreviado(contaDigital.saques)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">Pagamentos</p>
          <p className="text-2xl font-bold text-orange-600 mt-2">
            R$ {formatarNumeroAbreviado(contaDigital.pagamentos)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">Saldo Disponível</p>
          <p className="text-2xl font-bold text-green-600 mt-2">
            R$ {formatarNumeroAbreviado(contaDigital.saldoDisponivel)}
          </p>
        </div>
      </div>

      {/* Bloqueios e Regras */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-900 mb-4">Bloqueios de Segurança</h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-600">
              <i className="ri-shield-cross-line"></i>
            </div>
            <div>
              <p className="font-medium text-red-900">Destinatário ≠ premiado → BLOQUEADO</p>
              <p className="text-sm text-red-700">Pagamentos só podem ser feitos para premiados cadastrados</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-600">
              <i className="ri-shield-cross-line"></i>
            </div>
            <div>
              <p className="font-medium text-red-900">Destinatário = GETPOINTS → BLOQUEADO</p>
              <p className="text-sm text-red-700">Pagamentos para GETPOINTS devem ser feitos pela Conta Operacional</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-600">
              <i className="ri-shield-cross-line"></i>
            </div>
            <div>
              <p className="font-medium text-red-900">Uso exclusivo para premiados</p>
              <p className="text-sm text-red-700">Proibido pagamento de fornecedores, serviços ou agência</p>
            </div>
          </div>
        </div>
      </div>

      {/* Informações da Conta */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações da Conta</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600">Banco</p>
            <p className="font-medium text-gray-900 mt-1">Banco do Brasil</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Agência</p>
            <p className="font-medium text-gray-900 mt-1">1234-5</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Conta</p>
            <p className="font-medium text-gray-900 mt-1">12345-6</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Tipo</p>
            <p className="font-medium text-gray-900 mt-1">Conta Corrente Segregada</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Finalidade</p>
            <p className="font-medium text-gray-900 mt-1">Pagamento Exclusivo de Prêmios</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Status</p>
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800 mt-1">
              Ativa
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderExportacao = () => (
    <div className="space-y-6">
      {/* Exportação Manual */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Exportação Manual</h3>
        <p className="text-gray-600 mb-6">Selecione o formato desejado para exportar os dados contábeis</p>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {(['CSV', 'Excel', 'TXT', 'XML'] as const).map((formato) => (
            <div
              key={formato}
              onClick={() => setFormatoExportacao(formato)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formatoExportacao === formato
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  formatoExportacao === formato ? 'bg-blue-500' : 'bg-gray-100'
                }`}>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`ri-file-${formato === 'Excel' ? 'excel' : formato === 'TXT' ? 'text' : formato === 'XML' ? 'code' : 'list'}-line ${
                      formatoExportacao === formato ? 'text-white' : 'text-gray-600'
                    }`}></i>
                  </div>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{formato}</p>
                  <p className="text-xs text-gray-600">
                    {formato === 'TXT' ? 'Protheus' : formato === 'XML' ? 'Estruturado' : 'Planilha'}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => setModalExportacao(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          Exportar Dados
        </button>
      </div>

      {/* Campos de Exportação */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Campos Exportados</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            'Empresa',
            'Filial',
            'Data_Lancamento',
            'Conta_Debito',
            'Conta_Credito',
            'Centro_Custo',
            'Valor',
            'Historico',
            'Documento_Origem',
            'Campanha',
            'Agência (GETPOINTS)'
          ].map((campo) => (
            <div key={campo} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
              <div className="w-4 h-4 flex items-center justify-center text-emerald-600">
                <i className="ri-checkbox-circle-fill"></i>
              </div>
              <span className="text-sm text-gray-900">{campo}</span>
            </div>
          ))}
        </div>
      </div>

      {/* API Automática */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Exportação via API (Automática)</h3>
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Endpoint</span>
            <button className="text-blue-600 hover:text-blue-800 text-sm cursor-pointer whitespace-nowrap">
              Copiar
            </button>
          </div>
          <code className="text-sm text-gray-900 bg-white px-3 py-2 rounded border border-gray-200 block">
            POST /api/contabilidade/lancamentos
          </code>
        </div>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 flex items-center justify-center text-blue-600">
              <i className="ri-checkbox-circle-fill"></i>
            </div>
            <span className="text-sm text-gray-700">REST API</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 flex items-center justify-center text-blue-600">
              <i className="ri-checkbox-circle-fill"></i>
            </div>
            <span className="text-sm text-gray-700">Formato JSON</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 flex items-center justify-center text-blue-600">
              <i className="ri-checkbox-circle-fill"></i>
            </div>
            <span className="text-sm text-gray-700">Autenticação: Token / OAuth2</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 flex items-center justify-center text-blue-600">
              <i className="ri-checkbox-circle-fill"></i>
            </div>
            <span className="text-sm text-gray-700">Logs obrigatórios de todas as operações</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGuia = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6">
        <h3 className="text-xl font-semibold text-blue-900 mb-2">Guia Contábil e Financeiro</h3>
        <p className="text-blue-700">Orientações completas sobre o fluxo contábil e financeiro de prêmios</p>
      </div>

      {/* Fluxo Contábil Completo */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Fluxo Contábil Completo</h3>
        <div className="space-y-6">
          <div className="border-l-4 border-blue-500 pl-4">
            <h4 className="font-semibold text-gray-900 mb-2">ETAPA 1 – Aporte na Conta Prêmio</h4>
            <div className="bg-gray-50 rounded p-3 mb-2">
              <p className="text-sm text-gray-700"><strong>D:</strong> Caixa/Bancos – Conta Prêmio</p>
              <p className="text-sm text-gray-700"><strong>C:</strong> Caixa/Bancos – Conta Operacional</p>
            </div>
            <p className="text-sm text-gray-600">Transferência de recursos da conta operacional para a conta segregada de prêmios</p>
          </div>

          <div className="border-l-4 border-emerald-500 pl-4">
            <h4 className="font-semibold text-gray-900 mb-2">ETAPA 2 – Reconhecimento do Prêmio</h4>
            <div className="bg-gray-50 rounded p-3 mb-2">
              <p className="text-sm text-gray-700"><strong>D:</strong> Despesa com Prêmios</p>
              <p className="text-sm text-gray-700"><strong>C:</strong> Obrigações com Premiados</p>
            </div>
            <p className="text-sm text-gray-600">Reconhecimento da obrigação com os premiados no momento da distribuição</p>
          </div>

          <div className="border-l-4 border-purple-500 pl-4">
            <h4 className="font-semibold text-gray-900 mb-2">ETAPA 3 – Pagamento do Prêmio</h4>
            <div className="bg-gray-50 rounded p-3 mb-2">
              <p className="text-sm text-gray-700"><strong>D:</strong> Obrigações com Premiados</p>
              <p className="text-sm text-gray-700"><strong>C:</strong> Caixa/Bancos – Conta Prêmio</p>
            </div>
            <p className="text-sm text-gray-600">Baixa da obrigação através do pagamento via conta prêmio</p>
          </div>

          <div className="border-l-4 border-orange-500 pl-4">
            <h4 className="font-semibold text-gray-900 mb-2">ETAPA 4 – Serviços GETPOINTS</h4>
            <div className="bg-gray-50 rounded p-3 mb-2">
              <p className="text-sm text-gray-700 mb-2"><strong>Reconhecimento:</strong></p>
              <p className="text-sm text-gray-700">D: Despesa Serviços GETPOINTS</p>
              <p className="text-sm text-gray-700">C: Fornecedores GETPOINTS</p>
              <p className="text-sm text-gray-700 mt-2"><strong>Pagamento:</strong></p>
              <p className="text-sm text-gray-700">D: Fornecedores GETPOINTS</p>
              <p className="text-sm text-gray-700">C: Caixa/Bancos – Conta Operacional</p>
            </div>
            <p className="text-sm text-gray-600">Pagamento dos serviços da agência GETPOINTS via conta operacional</p>
          </div>
        </div>
      </div>

      {/* Regras Críticas */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Regras Críticas</h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-600">
              <i className="ri-close-circle-line"></i>
            </div>
            <div>
              <p className="font-medium text-red-900">Conta Prêmio - Uso Exclusivo</p>
              <p className="text-sm text-red-700">Proibido pagamento de fornecedores, serviços ou agência</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-red-600">
              <i className="ri-close-circle-line"></i>
            </div>
            <div>
              <p className="font-medium text-red-900">Agência Fixa e Imutável</p>
              <p className="text-sm text-red-700">Todas as notas fiscais são sempre da GETPOINTS</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 p-3 bg-emerald-50 rounded-lg">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-emerald-600">
              <i className="ri-checkbox-circle-line"></i>
            </div>
            <div>
              <p className="font-medium text-emerald-900">Segregação Obrigatória</p>
              <p className="text-sm text-emerald-700">Conta Prêmio segregada para pagamento de premiados</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 p-3 bg-emerald-50 rounded-lg">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-emerald-600">
              <i className="ri-checkbox-circle-line"></i>
            </div>
            <div>
              <p className="font-medium text-emerald-900">Conta Operacional para GETPOINTS</p>
              <p className="text-sm text-emerald-700">Pagamentos da agência sempre pela conta operacional</p>
            </div>
          </div>
        </div>
      </div>

      {/* Boas Práticas */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Boas Práticas</h3>
        <div className="space-y-2">
          {[
            'Conciliar diariamente os lançamentos contábeis com os pagamentos realizados',
            'Vincular todas as notas fiscais da GETPOINTS aos respectivos lotes',
            'Gerar lançamentos contábeis imediatamente após o pagamento dos prêmios',
            'Exportar dados para o ERP semanalmente ou conforme política da empresa',
            'Manter documentação completa de todos os lotes e pagamentos',
            'Revisar mensalmente o saldo da Conta Prêmio e obrigações em aberto',
            'Ativar alertas automáticos para divergências e pendências'
          ].map((pratica, index) => (
            <div key={index} className="flex items-start space-x-2">
              <div className="w-4 h-4 flex items-center justify-center mt-1 text-blue-600">
                <i className="ri-check-line"></i>
              </div>
              <p className="text-sm text-gray-700">{pratica}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <button 
                onClick={() => navigate('/admin/incentivo/pontuacao')}
                className="text-blue-600 hover:text-blue-800 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
              </button>
              <span className="text-sm text-gray-500">Pontuação</span>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <span className="text-sm text-gray-900">Controle de Prêmios & Conciliação</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Controle, Conciliação e Auditoria</h1>
            <p className="text-gray-600 mt-1">Gestão completa de prêmios, pagamentos e lançamentos contábeis</p>
          </div>
          <button
            onClick={() => setModalExportacao(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-download-line"></i>
            </div>
            <span>Exportar</span>
          </button>
        </div>

        {/* Abas */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {[
                { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
                { id: 'lotes', label: 'Lotes de Prêmios', icon: 'ri-gift-line' },
                { id: 'lancamentos', label: 'Lançamentos Contábeis', icon: 'ri-file-chart-line' },
                { id: 'notas', label: 'Notas GETPOINTS', icon: 'ri-file-text-line' },
                { id: 'conta', label: 'Conta Prêmio', icon: 'ri-bank-card-line' },
                { id: 'exportacao', label: 'Exportação', icon: 'ri-download-line' },
                { id: 'guia', label: 'Guia', icon: 'ri-book-line' }
              ].map((aba) => (
                <button
                  key={aba.id}
                  onClick={() => setAbaAtiva(aba.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm cursor-pointer whitespace-nowrap ${
                    abaAtiva === aba.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={aba.icon}></i>
                    </div>
                    <span>{aba.label}</span>
                  </div>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {abaAtiva === 'dashboard' && renderDashboard()}
            {abaAtiva === 'lotes' && renderLotesPremios()}
            {abaAtiva === 'lancamentos' && renderLancamentosContabeis()}
            {abaAtiva === 'notas' && renderNotasGetpoints()}
            {abaAtiva === 'conta' && renderContaPremio()}
            {abaAtiva === 'exportacao' && renderExportacao()}
            {abaAtiva === 'guia' && renderGuia()}
          </div>
        </div>
      </div>

      {/* Modal Vincular NF */}
      {modalVincularNF && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Vincular Nota Fiscal GETPOINTS</h3>
            <p className="text-gray-600 mb-4">Selecione a nota fiscal da GETPOINTS para vincular ao lote {loteParaVincular}</p>
            
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4">
              <option value="">Selecione uma nota fiscal...</option>
              {notasGetpoints.filter(n => n.status === 'Pendente').map((nota) => (
                <option key={nota.id} value={nota.id}>
                  {nota.tipo} - Nº {nota.numero} - R$ {nota.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </option>
              ))}
            </select>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setModalVincularNF(false);
                  setLoteParaVincular(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleVincularNF}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Vincular
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Exportação */}
      {modalExportacao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Exportar Dados Contábeis</h3>
            <p className="text-gray-600 mb-4">Os dados serão exportados no formato {formatoExportacao}</p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 text-blue-600">
                  <i className="ri-information-line"></i>
                </div>
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Dados incluídos na exportação:</p>
                  <ul className="space-y-1">
                    <li>• Todos os lançamentos contábeis</li>
                    <li>• Lotes de prêmios pagos</li>
                    <li>• Informações da campanha</li>
                    <li>• Agência GETPOINTS</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setModalExportacao(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleExportar}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Confirmar Exportação
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
